/**
 * Dark mode: the `.dark` class on <html> is the single source of truth
 * (Macondo's compiled CSS and our `dark:` utilities both key off it).
 * Adapted from spoonity-prism packages/shell/src/theme.ts. No pre-paint
 * inline script (the CSP forbids inline scripts); main.tsx calls
 * initTheme() before the first render instead.
 */

import { useCallback, useSyncExternalStore } from 'react';

export const THEME_KEY = 'billing_theme';
export type Theme = 'light' | 'dark';

const listeners = new Set<() => void>();

export function currentTheme(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function applyTheme(theme: Theme, persist = true): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  if (persist) {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* private mode: the class still applies */
    }
  }
  for (const l of listeners) l();
}

export function initTheme(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(THEME_KEY);
  } catch {
    /* ignore */
  }
  const prefersDark = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  applyTheme(stored === 'dark' || (stored === null && prefersDark) ? 'dark' : 'light', false);
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  return next;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { theme: Theme; toggleTheme: () => Theme } {
  const theme = useSyncExternalStore(subscribe, currentTheme, (): Theme => 'light');
  return { theme, toggleTheme: useCallback(toggleTheme, []) };
}
